import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaGift,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useAnnouncements } from "../../context/AnnouncementContext";

const AnnouncementPopup = ({ isDarkMode }) => {
  const { popupAnnouncement, dismissAnnouncement, markAsSeen, closePopup } =
    useAnnouncements();
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (popupAnnouncement) {
      markAsSeen(popupAnnouncement._id);
    }
  }, [popupAnnouncement, markAsSeen]);

  // Close popup without dismissing (just hides it for this session)
  const handleClose = () => {
    closePopup(); // This marks it as shown in sessionStorage
    setIsVisible(false);
  };

  // Dismiss announcement (permanently removes it)
  const handleDismiss = () => {
    if (popupAnnouncement) {
      dismissAnnouncement(popupAnnouncement._id);
      setIsVisible(false);
    }
  };

  const handleLinkClick = () => {
    if (popupAnnouncement?.link) {
      window.open(popupAnnouncement.link, "_blank");
    }
  };

  if (!isVisible || !popupAnnouncement) return null;

  const getTypeStyles = () => {
    const styles = {
      info: {
        bg: "linear-gradient(135deg, #3b82f6, #2563eb)",
        icon: <FaInfoCircle size={20} />,
        label: "Info",
      },
      success: {
        bg: "linear-gradient(135deg, #10b981, #059669)",
        icon: <FaCheckCircle size={20} />,
        label: "Success",
      },
      warning: {
        bg: "linear-gradient(135deg, #f59e0b, #d97706)",
        icon: <FaExclamationTriangle size={20} />,
        label: "Warning",
      },
      emergency: {
        bg: "linear-gradient(135deg, #ef4444, #dc2626)",
        icon: <FaExclamationTriangle size={20} />,
        label: "Emergency",
      },
      event: {
        bg: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
        icon: <FaCalendarAlt size={20} />,
        label: "Event",
      },
      promotion: {
        bg: "linear-gradient(135deg, #ec4899, #db2777)",
        icon: <FaGift size={20} />,
        label: "Promotion",
      },
    };
    return styles[popupAnnouncement.type] || styles.info;
  };

  const typeStyle = getTypeStyles();
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getBgColor = () => (isDarkMode ? "#1e1e2d" : "#ffffff");

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 30 }}
        transition={{ type: "spring", damping: 25 }}
        style={{
          maxWidth: "500px",
          width: "100%",
          background: getBgColor(),
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
          border: `1px solid ${isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)"}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: typeStyle.bg,
            padding: "20px",
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeStyle.icon}
            </div>
            <div>
              <span
                style={{
                  fontSize: "11px",
                  opacity: 0.9,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                {typeStyle.label}
              </span>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold" }}>
                {popupAnnouncement.title}
              </h3>
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: "24px" }}>
          {popupAnnouncement.imageUrl && (
            <img
              src={popupAnnouncement.imageUrl}
              alt={popupAnnouncement.title}
              style={{
                width: "100%",
                maxHeight: "150px",
                objectFit: "cover",
                borderRadius: "12px",
                marginBottom: "16px",
              }}
            />
          )}
          <p
            style={{
              fontSize: "14px",
              lineHeight: "1.6",
              color: getTextColor(),
              margin: "0 0 20px 0",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {popupAnnouncement.message}
          </p>

          {popupAnnouncement.link && (
            <button
              onClick={handleLinkClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 20px",
                background: typeStyle.bg,
                color: "white",
                border: "none",
                borderRadius: "12px",
                fontSize: "13px",
                fontWeight: "500",
                marginBottom: "20px",
                cursor: "pointer",
              }}
            >
              {popupAnnouncement.linkText || "Learn More"}
              <FaExternalLinkAlt size={12} />
            </button>
          )}

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={handleDismiss}
              style={{
                flex: 1,
                padding: "12px",
                background: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              Dismiss
            </button>
            <button
              onClick={handleClose}
              style={{
                flex: 1,
                padding: "12px",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                color: "white",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Footer */}
        {popupAnnouncement.expiresAt && (
          <div
            style={{
              padding: "12px 20px",
              borderTop: `1px solid ${isDarkMode ? "rgba(139,92,246,0.1)" : "#f0f0f0"}`,
              fontSize: "11px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              textAlign: "center",
            }}
          >
            <FaCalendarAlt size={10} style={{ marginRight: "6px" }} />
            Expires on{" "}
            {new Date(popupAnnouncement.expiresAt).toLocaleDateString()}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AnnouncementPopup;
